/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { jsx, css } from '@emotion/react';
import { EBrandingColorsOptions, EPickerTheme, ETypography, ETypographyColor, Icons } from '../../../types';
import { useColors } from '../../../hooks/useColors';
import { Typography } from '../../../atoms';
import { Button } from '../../../components/Button';
import { Pricify } from '../../../lib/price';
import { EDateFormats } from '../../../lib/timeUtils';
import dayjs from 'dayjs';
import { BnIcon } from '../../../components/icons_v2';
import { SkeletonWrapper } from '../../../components/skeletonWrapper';
import { marginTopCss, gapCss, flexCss } from '../../../styles/utils';
import { PaymentStatusEnum } from '../../../organisms/invoiceTemplate/types';
import { flexColCss } from '../../../organisms/refundPopup/style';
import { SizeEnum } from '../../../lib/constants';
import { cardContainerCss, infoCss, middleCss, paidLineCss } from './style';
import { CopyInvoiceLinkButton } from './CopyInvoiceLinkButton';
import { DownloadButton } from './DownloadButton';

const alignItemsCenterCss = css`
	align-items: center;
`;

const alignItemsStartCss = css`
	align-items: flex-start;
`;

const fullWidthCss = css`
	width: 100%;
`;

const fullWidthButtonCss = css`
	width: 100%;
	.brand {
		width: 100%;
	}
`;


export const CheckoutCard = ({
	memo,
	handlePayButton,
	organizationName,
	handleDownload,
	state,
	labels,
	isLoading,
	isPaymentPage,
	isPaymentMethodSelected = false,
}: {
	memo: string;
	handlePayButton: () => void;
	organizationName: string;
	handleDownload: (callback: () => void) => void;
	state: any;
	labels: any;
	isLoading: boolean;
	isPaymentPage: boolean;
	isPaymentMethodSelected?: boolean;
}) => {
	const { colors } = useColors();
	const totalToPay =
		!isPaymentPage && !isPaymentMethodSelected ? state.amountToPay : state?.amountWithFees || state?.amountToPay;

	const isFullyPaid = state?.paymentStatus === PaymentStatusEnum.FULLY_PAID;
	const isFullyCharged = state?.price === state?.paidAmount;

	const relevantLabels = labels.checkoutCard;

	return (
		<div data-aid="checkoutCard" css={cardContainerCss(colors)}>
			<div css={infoCss(colors)}>
				<div css={infoCss}>
					<div>
						{isPaymentMethodSelected && totalToPay - state?.paymentAmount > 0 && (
							<div css={[flexColCss, gapCss(4), marginTopCss(8)]}>
								<Typography type={ETypography.body1} color={ETypographyColor.secondary}>
									{relevantLabels.fee} {Pricify(totalToPay - state?.paymentAmount)}
								</Typography>
								<Typography type={ETypography.body1Accented} color={ETypographyColor.secondary}>
									{relevantLabels.subtotal} {Pricify(state?.paymentAmount)}
								</Typography>
							</div>
						)}
						<SkeletonWrapper isLoading={isLoading} minHeight={10}>
							{isPaymentMethodSelected ? (
								<Typography type={ETypography.h5} color={ETypographyColor.primary}>
									{relevantLabels.total} {Pricify(totalToPay)}
								</Typography>
							) : (
								<div>
									<Typography type={ETypography.body1Accented} color={ETypographyColor.primary}>
										{relevantLabels.balanceDue} 
									</Typography>
									<Typography type={ETypography.h5} color={ETypographyColor.primary}>
										{Pricify(totalToPay)}
									</Typography>
								</div>
							)}
						</SkeletonWrapper>
						{isFullyPaid && (
							<div css={paidLineCss}>
								<BnIcon icon={Icons.checkmark_circle} />
								<Typography type={ETypography.body1Accented} color={ETypographyColor.secondary}>
									{relevantLabels.paidOn} {dayjs(state?.updatedAt).format(EDateFormats.DAY_FORMAT)}
								</Typography>
							</div>
						)}
					</div>
					<div css={middleCss}>
						<SkeletonWrapper isLoading={isLoading} minHeight={6}>
							<Typography type={ETypography.body2Accented} color={ETypographyColor.primary}>
								{memo}
							</Typography>
						</SkeletonWrapper>
					</div>
				</div>
			</div>
			<div data-aid="checkoutCardButton" css={[flexColCss, alignItemsStartCss]}>
				<div css={[flexCss, alignItemsCenterCss]}>
					<DownloadButton label={relevantLabels.download} handleDownload={handleDownload} />
					{state?.isPublic && (
						<CopyInvoiceLinkButton
							labels={relevantLabels}
							invoiceId={state?.invoiceId}
							organizationId={state?.organizationId}
							userId={state?.userId}
						/>
					)}
				</div>
				{!isFullyCharged && (
					<div css={fullWidthCss}>
						<Button
							data-aid="button-checkoutCardButton-pay"
							sizer={SizeEnum.MEDIUM}
							disabledColor={EBrandingColorsOptions.tx_text_disabled}
							theme={!isPaymentPage ? EPickerTheme.BRAND : EPickerTheme.BASIC}
							disabled={isPaymentPage}
							onClick={handlePayButton}
							css={fullWidthButtonCss}
						>
							{relevantLabels.pay}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
